"use client";

import type { Dispatch, FormEvent, SetStateAction } from "react";
import { useRouter } from "next/navigation";
import { generateKundali, RetryExhaustedError, type KundaliResponse } from "@/lib/api/kundali";
import {
  trackKundaliFormSubmitted,
  trackKundaliFormSubmitFailed,
  trackKundaliFieldOutcome,
  trackKundaliApiAttempt,
  type KundaliFieldOutcome,
} from "@/lib/analytics-kundali";
import { newIdempotencyKey } from "@/lib/api/_retry";
import type { PlaceResult } from "@/lib/api/misc";
import {
  currentPathname,
  finishKundali,
  handleInvalidKundaliForm,
} from "./KundaliFormCard.actions";
import { resolveTypedPlace } from "./KundaliFormCard.place-resolve";
import type { useDeliveryCapture } from "./KundaliFormCard.delivery";
import { persistFormContact } from "./KundaliFormCard.contact-capture";
import type { CapturedSEOEntry } from "./KundaliFormCard.parts";
import type { KundaliLang } from "./KundaliFormCard.i18n";
import {
  validateKundaliForm,
  normalizeBirthDateInput,
  type KundaliFieldErrors,
} from "./KundaliFormCard.validation";
import { isValidIndianPhone, isValidOptionalEmail } from "@/lib/phone-india";

/** Retry state surfaced to the UI when the API is unreachable after N attempts. */
export type KundaliRetryState = { idempotencyKey: string; cause: string } | null;

export type HandleSubmitDeps = {
  seoEntry: CapturedSEOEntry | null;
  source: string;
  requireBirthTime: boolean;
  requireContact: boolean;
  name: string;
  phone: string;
  email: string;
  birthDate: string;
  birthTime: string;
  birthPlace: string;
  language: KundaliLang;
  selectedPlace: PlaceResult | null;
  delivery: ReturnType<typeof useDeliveryCapture>;
  router: ReturnType<typeof useRouter>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setError: Dispatch<SetStateAction<string | null>>;
  setFieldErrors: Dispatch<SetStateAction<KundaliFieldErrors>>;
  setResult: Dispatch<SetStateAction<KundaliResponse | null>>;
  setSelectedPlace: Dispatch<SetStateAction<PlaceResult | null>>;
  setAmbiguousSuggestion: Dispatch<SetStateAction<PlaceResult | null>>;
  setRetryState: Dispatch<SetStateAction<KundaliRetryState>>;
};

function resolveAnalyticsSource(seoEntry: CapturedSEOEntry | null, source: string) {
  return {
    analyticsSource: seoEntry ? `seo:${seoEntry.config.intent}` : source,
    sourcePath: seoEntry?.sourcePath || currentPathname(),
  };
}

/**
 * Per-field outcome telemetry at submit time — splits ambiguity vs error vs
 * empty PER FIELD so a dbt mart over events_raw can finally attribute the
 * failure (the conflation the 31,749-failure leak hid). `placeOutcome` is
 * decided upstream (the network-aware resolver) and passed in.
 */
function emitFieldOutcomes(
  d: HandleSubmitDeps,
  placeOutcome: KundaliFieldOutcome,
  analyticsSource: string,
  sourcePath: string | undefined
) {
  const meta = { source: analyticsSource, source_path: sourcePath, language: d.language };
  const fire = (
    field: Parameters<typeof trackKundaliFieldOutcome>[0]["field"],
    outcome: KundaliFieldOutcome
  ) => trackKundaliFieldOutcome({ field, outcome, ...meta });

  fire("name", d.name.trim() ? "ok" : "empty");
  fire("birthDate", birthDateOutcome(d.birthDate));
  if (d.requireBirthTime) fire("birthTime", d.birthTime.trim() ? "ok" : "empty");
  fire("birthPlace", placeOutcome);
  if (d.requireContact || d.phone.trim()) fire("phone", phoneOutcome(d.phone, d.requireContact));
  if (d.email.trim()) fire("email", isValidOptionalEmail(d.email) ? "ok" : "invalid");
}

function birthDateOutcome(value: string): KundaliFieldOutcome {
  if (!value.trim()) return "empty";
  return normalizeBirthDateInput(value) ? "ok" : "invalid";
}

function phoneOutcome(value: string, required: boolean): KundaliFieldOutcome {
  if (!value.trim()) return required ? "empty" : "ok";
  return isValidIndianPhone(value) ? "ok" : "invalid";
}

/**
 * Resolve the effective birth place: a tapped chip wins; otherwise resolve the
 * typed text with the network-aware resolver and act on the discriminated
 * outcome. Returns the place to submit with, or null when the submit must abort
 * (ambiguous / network_error / empty) — each having set the right UI state +
 * emitted the right field outcome so the funnel can tell them apart.
 */
async function resolveEffectivePlace(
  d: HandleSubmitDeps,
  analyticsSource: string,
  sourcePath: string | undefined
): Promise<{ place: PlaceResult | null; outcome: KundaliFieldOutcome; abort: boolean }> {
  if (d.selectedPlace) return { place: d.selectedPlace, outcome: "ok", abort: false };
  if (!d.birthPlace.trim()) return { place: null, outcome: "empty", abort: false };

  const resolution = await resolveTypedPlace(d.birthPlace);
  if (resolution.kind === "resolved") {
    // A confident typed match → commit the chip so the user SEES it locked.
    d.setSelectedPlace(resolution.place);
    d.setAmbiguousSuggestion(null);
    return { place: resolution.place, outcome: "ok", abort: false };
  }
  if (resolution.kind === "ambiguous") {
    d.setAmbiguousSuggestion(resolution.suggestion);
    return { place: null, outcome: "ambiguous", abort: false };
  }
  if (resolution.kind === "network_error") {
    // Network fault, NOT a bad city — surface the retry card, not a place error.
    d.setRetryState({ idempotencyKey: newIdempotencyKey(), cause: resolution.cause });
    trackKundaliFieldOutcome({
      field: "birthPlace",
      outcome: "network_error",
      source: analyticsSource,
      source_path: sourcePath,
      language: d.language,
    });
    return { place: null, outcome: "network_error", abort: true };
  }
  return { place: null, outcome: "empty", abort: false };
}

/**
 * The generate → (delivery capture) → render/navigate step. Threads the same
 * idempotency key + per-attempt telemetry into the resilient client, and on
 * exhaustion sets the retry state (reusing the SAME key) so the UI shows a
 * "Try again" card instead of a dead-end string.
 */
async function runKundaliGeneration(
  d: HandleSubmitDeps,
  birthDateIso: string,
  idempotencyKey: string,
  analyticsSource: string,
  sourcePath: string | undefined
) {
  let navigated = false;
  try {
    const response = await generateKundali(
      {
        name: d.name,
        birth_date: birthDateIso,
        birth_time: d.birthTime || null,
        birth_place: d.birthPlace,
        language: d.language,
        ...(d.selectedPlace && {
          latitude: d.selectedPlace.latitude,
          longitude: d.selectedPlace.longitude,
          timezone: d.selectedPlace.timezone,
        }),
      },
      {
        idempotencyKey,
        onAttempt: (attempt, outcome) =>
          trackKundaliApiAttempt({
            attempt,
            outcome,
            source: analyticsSource,
            source_path: sourcePath,
            language: d.language,
          }),
      }
    );
    void d.delivery.persist(response.id); // form-level delivery capture (soft-fail)
    void persistFormContact(response.id, d.phone, d.email); // chat-entry contact (soft-fail)
    const ctx = {
      source: d.source,
      language: d.language,
      birthTime: d.birthTime,
      analyticsSource,
      sourcePath,
    };
    navigated = finishKundali(response, ctx, d.router, d.setResult);
    d.setRetryState(null);
  } catch (err) {
    handleGenerationError(d, err, idempotencyKey, analyticsSource, sourcePath);
  } finally {
    if (!navigated) d.setLoading(false);
  }
}

function handleGenerationError(
  d: HandleSubmitDeps,
  err: unknown,
  idempotencyKey: string,
  analyticsSource: string,
  sourcePath: string | undefined
) {
  const message = err instanceof Error ? err.message : "Something went wrong";
  if (err instanceof RetryExhaustedError) {
    // Unreachable after N attempts → user-facing retry state (reuse the key).
    d.setRetryState({ idempotencyKey, cause: err.lastCause });
  } else {
    d.setError(message);
  }
  trackKundaliFormSubmitFailed({
    language: d.language,
    has_time: !!d.birthTime,
    source: analyticsSource,
    source_path: sourcePath,
    error_message: message,
  });
}

/**
 * Shared core for both a fresh submit and a "Try again" retry. `idempotencyKey`
 * is minted fresh for a new submit and reused for a retry so a re-send can never
 * double-create server-side.
 */
async function submitKundali(d: HandleSubmitDeps, idempotencyKey: string) {
  const { analyticsSource, sourcePath } = resolveAnalyticsSource(d.seoEntry, d.source);

  const place = await resolveEffectivePlace(d, analyticsSource, sourcePath);
  if (place.abort) {
    d.setLoading(false);
    return;
  }
  const effectivePlace = place.place;

  const validation = validateKundaliForm({
    birthDate: d.birthDate,
    birthTime: d.birthTime,
    birthPlace: d.birthPlace,
    language: d.language,
    name: d.name,
    phone: d.phone,
    email: d.email,
    placeSelected: Boolean(effectivePlace),
    requireBirthTime: d.requireBirthTime,
    requireContact: d.requireContact,
  });
  emitFieldOutcomes(d, place.outcome, analyticsSource, sourcePath);
  if (!validation.ok) {
    handleInvalidKundaliForm({
      validation,
      setFieldErrors: d.setFieldErrors,
      setError: d.setError,
      language: d.language,
      birthTime: d.birthTime,
      analyticsSource,
      sourcePath,
    });
    d.setLoading(false);
    return;
  }
  d.setError(null);
  d.setFieldErrors({});
  d.setRetryState(null);
  trackKundaliFormSubmitted({
    language: d.language,
    has_time: !!d.birthTime,
    source: analyticsSource,
    source_path: sourcePath,
  });
  await runKundaliGeneration(
    { ...d, selectedPlace: effectivePlace },
    validation.birthDateIso,
    idempotencyKey,
    analyticsSource,
    sourcePath
  );
}

/**
 * Submit handler — mints ONE idempotency key per logical submit. Closes over
 * the form state via `d`.
 */
export function makeHandleSubmit(d: HandleSubmitDeps) {
  return async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    d.setLoading(true);
    await submitKundali(d, newIdempotencyKey());
  };
}

/**
 * "Try again" handler for the retry card — re-runs the submit with the SAME
 * idempotency key so the re-send dedupes server-side.
 */
export function makeRetryKundali(d: HandleSubmitDeps, idempotencyKey: string) {
  return async function retryKundali() {
    d.setLoading(true);
    await submitKundali(d, idempotencyKey);
  };
}
